import {compact, isSubset} from '@benchling/js-utils';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {
  FieldDefinitionNode,
  ProcessFlowchartNodeDetailsType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  FlowchartEditorMode,
  isExecutionMode,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {getFieldInfoFromDataFlowEdgeHandle} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/flowchartIdentifierUtils';
import {
  ProcessFlowchartEdgeType,
  TaskSchemaNodeSectionType,
  TransitionSource,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {buildTransitionIndexByPTDVPairWithSubsets} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/types/transition/transitionKeyUtils';
import {Transition} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/types/transition/TransitionTypes';
import {
  areFieldDefinitionsCompatible,
  FieldDefinitionForValidation,
} from 'schemas/fields/FieldDefinitionCompatibilityUtils';
import {detectCyclesWithNode} from 'util/graph-utils/detectCycles';
import {getAdjacencyMapFromProcessFlowchart} from 'util/graph-utils/getFlowchartAdjacencyMap';
import getLoopIgnoringAncestorsAndEdges from 'util/graph-utils/getLoopIgnoringAncestorsAndEdges';
import {AdjacencyMap} from 'util/graph-utils/GraphUtilConstants';

import {hasMatchingControlFlowEdge} from '../flowchart-viz/horizontal-flowchart/edges/edgeUtils';
import {PLUGIN_REGISTRY_INSTANCE} from '../platform/PluginRegistry';
import {
  FlowchartValidationSeverity,
  ProcessFlowchartErrorType,
  ProcessFlowchartObjectType,
  ProcessFlowchartValidationError,
  MaybeValidationError,
  DataFlowEdgeForValidation,
  ProcessTaskSchemaNodeForValidation,
  FlowchartValidationErrorType,
  ValidationContext,
} from './ProcessFlowchartForValidationTypes';
import {
  ProcessEdgeForValidation,
  ProcessNodeForValidation,
  ProcessFlowchartForValidation,
} from './ProcessFlowchartForValidationTypes';
import {ProcessFlowchartDesignSpace} from './ProcessGraphBuilderConstants';
import {
  getEdgeDisplayName,
  getFlowchartNodeFromClientId,
  getFulfillingMapperConfigs,
  getInboundEdges,
  getNodesByClientId,
  getOutboundEdges,
  getSchemaIdFromNodeClientId,
  getTaskNodesDownstreamOfNode,
  getTaskNodesUpstreamOfNode,
  getTemplateNodeFromClientId,
  getTemplateNodeFromSchemaId,
} from './ProcessGraphGetters';

const doesNodeListContainNodeByClientId = (clientId: string, nodeList: Array<{id: string}>): boolean =>
  nodeList.find((node) => node.id === clientId) != null;

/**
 * Returns true if there is at least one configured transition, all configured
 * transitions are optional, and ACM or PCM is enabled. Used to allow the canvas
 * to be empty (only Start/End nodes) when only optional transitions exist.
 */
export function hasOnlyOptionalTransitions(
  transitions: Record<string, Transition>,
  isFlowchartModifiableOnCreation = false,
  isFlowchartModifiableAfterCreation = false,
  editorMode: FlowchartEditorMode = FlowchartEditorMode.MAIN_CANVAS
): boolean {
  if (isExecutionMode(editorMode)) {
    return false;
  }
  const transitionList = Object.values(transitions);
  const acmOrPcmEnabled = isFlowchartModifiableOnCreation || isFlowchartModifiableAfterCreation;
  return transitionList.length > 0 && transitionList.every((t) => !t.isPlanned) && acmOrPcmEnabled;
}

/**
 * Editor contexts where structural flowchart validation rules apply.
 * Structural rules (like requiring root nodes, single entry points, etc.) only apply
 * when editing a full flowchart, not when editing a single transition.
 */
const STRUCTURAL_VALIDATION_MODES: Array<FlowchartEditorMode> = [
  FlowchartEditorMode.MAIN_CANVAS,
  FlowchartEditorMode.ACM_EDITOR,
  FlowchartEditorMode.PCM_EDITOR,
];

/**
 * Editor contexts where data-flow-edge validation rules apply. Data flow editing is
 * disabled in ACM/PCM (the `SHOW_NODE_SECTIONS_AND_DATA_FLOW_EDGES` feature is off in
 * those modes), so errors produced by data-flow rules are suppressed there via
 * `enabledModes`.
 */
export const DATA_FLOW_VALIDATION_MODES: Array<FlowchartEditorMode> = [
  FlowchartEditorMode.MAIN_CANVAS,
  FlowchartEditorMode.SINGLE_TRANSITION_CANVAS,
];

const doesNodeListContainNodeBySchemaId = (schemaId: string, nodeList: Array<{schemaId: string}>): boolean =>
  nodeList.find((node) => node.schemaId === schemaId) != null;

export function ensureFlowchartHasNoInaccessibleNodes(
  validationContext: ValidationContext,
  flowchartId: string
): MaybeValidationError {
  if ((validationContext.inaccessibleNodeIds ?? []).length === 0) {
    return null;
  }

  return {
    object: {
      clientId: flowchartId,
      type: ProcessFlowchartObjectType.FLOWCHART_CONFIG,
    },
    errorMessage: "This flowchart contains one or more task nodes you don't have access to.",
    errorType: ProcessFlowchartErrorType.NODE_NOT_AVAILABLE,
    blocksPersistence: true,
  };
}

function buildNodeIdToPTDVIdMap(nodes: Array<ProcessNodeForValidation>): Map<string, string> {
  const nodeIdToPTDVId = new Map<string, string>();
  for (const node of nodes) {
    if (isTaskSchemaNode(node) && node.taskDefinitionVersionSpec?.clientId != null) {
      nodeIdToPTDVId.set(node.id, node.taskDefinitionVersionSpec.clientId);
    }
  }
  return nodeIdToPTDVId;
}

/**
 * Resolves a node to a single upstream task PTDV. Task nodes resolve to their own PTDV;
 * router nodes resolve to the PTDV of their single upstream task (routers are constrained
 * to one incoming edge by `ensureRouterNodeHasOneIncomingEdge`). Returns `null` if the
 * node has no resolvable upstream task PTDV (e.g. an unconfigured node, or a router with
 * no upstream task).
 */
export function resolveUpstreamPTDV(
  nodeId: string,
  flowchart: ProcessFlowchartForValidation,
  nodeIdToPTDVId: Map<string, string>
): string | null {
  const directPTDV = nodeIdToPTDVId.get(nodeId);
  if (directPTDV != null) {
    return directPTDV;
  }
  const node = getFlowchartNodeFromClientId(flowchart, nodeId);
  if (node?.nodeType !== ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS) {
    return null;
  }
  for (const upstreamTask of getTaskNodesUpstreamOfNode(nodeId, flowchart)) {
    const ptdv = nodeIdToPTDVId.get(upstreamTask.id);
    if (ptdv != null) {
      return ptdv;
    }
  }
  return null;
}

/**
 * Resolves a node to the set of downstream task PTDVs reachable from it. Task nodes
 * resolve to a single-element array containing their own PTDV; router nodes can fan out
 * to multiple downstream task nodes, so they resolve to one PTDV per reachable
 * downstream task. Returns an empty array if the node has no resolvable downstream
 * task PTDVs.
 */
export function resolveDownstreamPTDVs(
  nodeId: string,
  flowchart: ProcessFlowchartForValidation,
  nodeIdToPTDVId: Map<string, string>
): Array<string> {
  const directPTDV = nodeIdToPTDVId.get(nodeId);
  if (directPTDV != null) {
    return [directPTDV];
  }
  const node = getFlowchartNodeFromClientId(flowchart, nodeId);
  if (node?.nodeType !== ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS) {
    return [];
  }
  const ptdvs: Array<string> = [];
  for (const downstreamTask of getTaskNodesDownstreamOfNode(nodeId, flowchart)) {
    const ptdv = nodeIdToPTDVId.get(downstreamTask.id);
    if (ptdv != null) {
      ptdvs.push(ptdv);
    }
  }
  return ptdvs;
}

/**
 * Returns the set of source PTDV(s) in sourcePTDVIdSet that are upstream of a control flow edge.
 * Task nodes map to their PTDV; router nodes map to upstream task PTDVs that
 * appear in the transition.
 */
function getSourcePTDVsUpstreamOfControlFlowEdge(
  edge: ProcessEdgeForValidation,
  flowchart: ProcessFlowchartForValidation,
  nodeIdToPTDVId: Map<string, string>,
  sourcePTDVIdSet: Set<string>
): Set<string> {
  const directPtdv = nodeIdToPTDVId.get(edge.source);
  if (directPtdv != null) {
    if (sourcePTDVIdSet.has(directPtdv)) {
      return new Set([directPtdv]);
    }
    return new Set();
  }
  const sourceNode = getFlowchartNodeFromClientId(flowchart, edge.source);
  if (sourceNode?.nodeType === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS) {
    const upstreamPTDVs = new Set<string>();
    for (const task of getTaskNodesUpstreamOfNode(edge.source, flowchart)) {
      const ptdv = nodeIdToPTDVId.get(task.id);
      if (ptdv != null && sourcePTDVIdSet.has(ptdv)) {
        upstreamPTDVs.add(ptdv);
      }
    }
    return upstreamPTDVs;
  }
  return new Set();
}

/**
 * Checks if a validation node is a task schema node (has task definition version).
 */
function isTaskSchemaNode(node: ProcessNodeForValidation): node is ProcessTaskSchemaNodeForValidation {
  return (
    node.nodeType === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS ||
    node.nodeType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS ||
    node.nodeType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
  );
}

function getFieldFromHandle(
  handleId: string,
  node: ProcessTaskSchemaNodeForValidation
): FieldDefinitionNode | null {
  const fieldClientInfo = getFieldInfoFromDataFlowEdgeHandle(handleId);

  const isOutputNode =
    node.nodeType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS ||
    fieldClientInfo.sectionType === TaskSchemaNodeSectionType.OUTPUTS;

  const fieldDefinitions = isOutputNode
    ? node.taskDefinitionVersionSpec.outputFieldDefinitions
    : node.taskDefinitionVersionSpec.inputFieldDefinitions;

  const fieldDef = fieldDefinitions?.find(
    (field) => field.fieldClientIdentifier === fieldClientInfo.fieldClientId
  );

  return (fieldDef?.fieldDefinition as FieldDefinitionNode) ?? null;
}

interface ValidationCallback {
  nodeId?: string;
  edgeId?: string;
}

export function getDisplayNameForNode(
  node: ProcessNodeForValidation,
  isFlowchartFirstConfig: boolean
): string {
  if (
    isFlowchartFirstConfig &&
    BenchlingConfig.WORKFLOWS_IMPROVED_FLOWCHART_CONFIG &&
    isTaskSchemaNode(node)
  ) {
    return PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(node.executionType).typeDisplayName;
  }
  // Default type display names if not task schema node or not flowchart-first config
  switch (node.nodeType) {
    case ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS:
      return 'task';
    case ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS:
      return 'root';
    case ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS:
      return 'router';
    case ProcessFlowchartNodeDetailsType.END_NODE_DETAILS:
      return 'stop';
    case ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS:
      return 'end';
    default:
      return 'node';
  }
}

// Edge errors are shown on the destination node, if possible, as the current UI doesn't have a way
// to show errors on edges directly.
export function getValidationErrorForEdge(
  flowchart: ProcessFlowchartForValidation,
  edge: ProcessEdgeForValidation,
  errorMessage: (callbackProps: ValidationCallback) => string,
  edgeErrorType: FlowchartValidationErrorType
): ProcessFlowchartValidationError {
  const destinationNode = getFlowchartNodeFromClientId(flowchart, edge.target);
  const destinationNodeId = destinationNode?.id;
  return {
    object: {
      clientId: destinationNodeId ?? edge.id,
      type: destinationNode ? ProcessFlowchartObjectType.NODE : ProcessFlowchartObjectType.EDGE,
    },
    errorMessage: errorMessage({nodeId: destinationNodeId, edgeId: edge.id}),
    errorType: edgeErrorType,
    blocksPersistence: true,
  };
}

function edgeConnectsNodesInFlowchart(
  edge: ProcessEdgeForValidation,
  flowchart: ProcessFlowchartForValidation
): boolean {
  const fromNode = getFlowchartNodeFromClientId(flowchart, edge.source);
  const toNode = getFlowchartNodeFromClientId(flowchart, edge.target);
  return (
    fromNode != null &&
    toNode != null &&
    doesNodeListContainNodeByClientId(fromNode.id, flowchart.nodes) &&
    doesNodeListContainNodeByClientId(toNode.id, flowchart.nodes)
  );
}

export function ensureNodeIsInsideDesignSpace(
  node: ProcessNodeForValidation,
  designSpace: ProcessFlowchartDesignSpace
): MaybeValidationError {
  return node.nodeType === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS &&
    node.taskSchemaId !== designSpace.flowchartSchema.id &&
    !doesNodeListContainNodeBySchemaId(node.taskSchemaId, designSpace.availableNodes)
    ? {
        object: {
          clientId: node.id,
          type: ProcessFlowchartObjectType.NODE,
        },
        errorMessage: `This node is not available for this flowchart.`,
        errorType: ProcessFlowchartErrorType.NODE_NOT_AVAILABLE,
        blocksPersistence: true,
      }
    : null;
}

export function ensureNodeMappersFulfilled(
  node: ProcessNodeForValidation,
  flowchart: ProcessFlowchartForValidation,
  designSpace: ProcessFlowchartDesignSpace,
  usingFlowchartFirstConfig?: boolean
): MaybeValidationError {
  // Root, router, and end nodes cannot be mapped to. So we don't need to validate that their mappers are fulfilled.
  if (
    node.nodeType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS ||
    node.nodeType === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS ||
    node.nodeType === ProcessFlowchartNodeDetailsType.END_NODE_DETAILS ||
    usingFlowchartFirstConfig
  ) {
    return null;
  }

  // The associated template node is guaranteed to exist by ensureNodeIsInsideDesignSpace. But we
  // double-check, in case the tests are out of order
  const templateNode = getTemplateNodeFromSchemaId(designSpace.availableNodes, node.taskSchemaId);
  if (templateNode == null) {
    return null;
  }

  // We don't throw an error if the node hasn't been connected yet (this error will be reported elsewhere)
  const incomingEdges = flowchart.edges.filter((edge) => edge.target === node.id);
  if (incomingEdges.length === 0) {
    return null;
  }

  const fulfillingMapperConfigs = getFulfillingMapperConfigs(node, flowchart, templateNode);

  return fulfillingMapperConfigs != null
    ? null
    : {
        object: {
          clientId: node.id,
          type: ProcessFlowchartObjectType.NODE,
        },
        errorMessage: `This node is missing required paths.`,
        errorType: ProcessFlowchartErrorType.NODE_MISSING_REQUIRED_PATH,
        blocksPersistence: true,
        severity: FlowchartValidationSeverity.WARNING,
      };
}

interface EnsureNodeIsConnectedParams {
  node: ProcessNodeForValidation;
  nodes: Array<ProcessNodeForValidation>;
  edges: Array<ProcessEdgeForValidation>;
  usingFlowchartFirstConfig: boolean;
  transitions: Record<string, Transition>;
  isFlowchartModifiableOnCreation?: boolean;
  isFlowchartModifiableAfterCreation?: boolean;
  editorMode?: FlowchartEditorMode;
}

export function ensureNodeIsConnected(params: EnsureNodeIsConnectedParams): MaybeValidationError {
  const {
    node,
    nodes,
    edges,
    usingFlowchartFirstConfig,
    transitions,
    isFlowchartModifiableOnCreation,
    isFlowchartModifiableAfterCreation,
    editorMode = FlowchartEditorMode.MAIN_CANVAS,
  } = params;
  let nodeError = null;
  switch (node.nodeType) {
    case ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS:
      nodeError = {
        object: {
          clientId: node.id,
          type: ProcessFlowchartObjectType.NODE,
        },
        errorMessage: 'Connect to 1 source and at least 2 destinations to configure this router',
        errorType: ProcessFlowchartErrorType.ROUTER_NOT_CONNECTED,
        blocksPersistence: true,
        severity: FlowchartValidationSeverity.WARNING,
      };
      break;
    case ProcessFlowchartNodeDetailsType.END_NODE_DETAILS:
      nodeError = {
        object: {
          clientId: node.id,
          type: ProcessFlowchartObjectType.NODE,
        },
        errorMessage: 'Connect this stop to one router as its source.',
        errorType: ProcessFlowchartErrorType.END_NODE_NOT_CONNECTED,
        blocksPersistence: true,
        severity: FlowchartValidationSeverity.WARNING,
      };
      break;
    case ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS:
      if (
        !hasOnlyOptionalTransitions(
          transitions,
          isFlowchartModifiableOnCreation,
          isFlowchartModifiableAfterCreation,
          editorMode
        )
      ) {
        nodeError = {
          object: {
            clientId: node.id,
            type: ProcessFlowchartObjectType.NODE,
          },
          errorMessage: 'Connect the End to the rest of the flowchart.',
          errorType: ProcessFlowchartErrorType.OUTPUT_NODE_NOT_CONNECTED,
          blocksPersistence: true,
          severity: FlowchartValidationSeverity.WARNING,
        };
      }
      break;
    case ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS:
      if (
        !hasOnlyOptionalTransitions(
          transitions,
          isFlowchartModifiableOnCreation,
          isFlowchartModifiableAfterCreation,
          editorMode
        )
      ) {
        nodeError = {
          object: {
            clientId: node.id,
            type: ProcessFlowchartObjectType.NODE,
          },
          errorMessage: 'Connect the Start to the rest of the flowchart.',
          errorType: ProcessFlowchartErrorType.ROOT_NODE_NOT_CONNECTED,
          blocksPersistence: true,
          severity: FlowchartValidationSeverity.WARNING,
        };
      }
      break;
    case ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS:
      nodeError = {
        object: {
          clientId: node.id,
          type: ProcessFlowchartObjectType.NODE,
        },
        errorMessage: `Connect this ${getDisplayNameForNode(node, usingFlowchartFirstConfig)} to the rest of the flowchart.`,
        errorType: ProcessFlowchartErrorType.TASK_NODE_NOT_CONNECTED,
        blocksPersistence: true,
        severity: FlowchartValidationSeverity.WARNING,
      };
      break;
    default:
      nodeError = null;
  }
  // We give a special pass to one-node flowcharts
  const nodeIsConnected =
    nodes.length === 1 ||
    edges.find(
      (edge) =>
        (edge.source === node.id || edge.target === node.id) &&
        edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW
    ) != null;
  return !nodeIsConnected ? nodeError : null;
}

export function ensureFlowchartHasTaskNode(nodes: Array<ProcessNodeForValidation>): MaybeValidationError {
  const taskNode = nodes.find((node) => node.nodeType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS);
  return taskNode == null
    ? {
        object: null,
        errorMessage: "This flowchart needs a node for the flowchart's task schema.",
        errorType: ProcessFlowchartErrorType.NO_FLOWCHART_TASK_NODE,
        blocksPersistence: true,
        enabledModes: STRUCTURAL_VALIDATION_MODES,
      }
    : null;
}

export function ensureFlowchartHasIntermediateNodes(
  nodes: Array<ProcessNodeForValidation>,
  transitions: Record<string, Transition>,
  isFlowchartModifiableOnCreation = false,
  isFlowchartModifiableAfterCreation = false,
  editorMode: FlowchartEditorMode = FlowchartEditorMode.MAIN_CANVAS
): MaybeValidationError {
  const rootNode = nodes.find((node) => node.nodeType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS);
  if (rootNode == null) {
    return null;
  }
  if (
    !nodes.some((node) => node.nodeType === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS) &&
    // Allow flowcharts that have only optional transitions with no canvas nodes
    !hasOnlyOptionalTransitions(
      transitions,
      isFlowchartModifiableOnCreation,
      isFlowchartModifiableAfterCreation,
      editorMode
    )
  ) {
    return {
      object: {
        clientId: rootNode.id,
        type: ProcessFlowchartObjectType.NODE,
      },
      errorMessage: 'Add a node after Start.',
      errorType: ProcessFlowchartErrorType.NO_INTERMEDIATE_NODES,
      blocksPersistence: true,
      severity: FlowchartValidationSeverity.WARNING,
      enabledModes: STRUCTURAL_VALIDATION_MODES,
    };
  }
  return null;
}

interface EnsureFlowchartHasSingleEntryPointParams {
  nodes: Array<ProcessNodeForValidation>;
  edges: Array<ProcessEdgeForValidation>;
  usingFlowchartFirstConfig: boolean;
  transitions: Record<string, Transition>;
  isFlowchartModifiableOnCreation?: boolean;
  isFlowchartModifiableAfterCreation?: boolean;
  editorMode?: FlowchartEditorMode;
}

export function ensureFlowchartHasSingleEntryPoint(
  params: EnsureFlowchartHasSingleEntryPointParams
): Array<ProcessFlowchartValidationError> | null {
  const {
    nodes,
    edges,
    usingFlowchartFirstConfig,
    transitions,
    isFlowchartModifiableOnCreation,
    isFlowchartModifiableAfterCreation,
    editorMode = FlowchartEditorMode.MAIN_CANVAS,
  } = params;
  const startNodes = nodes.filter(
    (node) =>
      edges.find(({target, type}) => target === node.id && type === ProcessFlowchartEdgeType.CONTROL_FLOW) ==
      null
  );
  return startNodes.length > 1
    ? startNodes
        .filter(
          (startNode) =>
            startNode.nodeType !== ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS &&
            // If the flowchart has only optional transitions, the output node has no incoming edges
            // but is not a real second entry point
            !(
              startNode.nodeType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS &&
              hasOnlyOptionalTransitions(
                transitions,
                isFlowchartModifiableOnCreation,
                isFlowchartModifiableAfterCreation,
                editorMode
              )
            )
        )
        .map((startNode) => ({
          object: {
            clientId: startNode.id,
            type: ProcessFlowchartObjectType.NODE,
          },
          errorMessage: `Flowcharts can only have 1 starting point. Draw a connection to this ${getDisplayNameForNode(startNode, usingFlowchartFirstConfig)}.`,
          errorType: ProcessFlowchartErrorType.TOO_MANY_START_NODES,
          blocksPersistence: true,
          enabledModes: STRUCTURAL_VALIDATION_MODES,
        }))
    : null;
}

interface EnsureFlowchartHasOutputNodeParams {
  nodes: Array<ProcessNodeForValidation>;
  designSpace: ProcessFlowchartDesignSpace;
}

export function ensureFlowchartHasOutputNode(params: EnsureFlowchartHasOutputNodeParams): {
  error: MaybeValidationError;
  outputNode: ProcessNodeForValidation | null;
} {
  const {nodes, designSpace} = params;
  // If the flowchart schema has outputs, we need an output node, and we need it to have an incoming edge
  const outputNode =
    nodes.find((node) => node.nodeType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS) ?? null;
  if (designSpace.flowchartSchema.outputSchema != null) {
    if (outputNode == null) {
      return {
        error: {
          object: null,
          errorMessage: "This flowchart needs a node for the flowchart's output schema.",
          errorType: ProcessFlowchartErrorType.NO_OUTPUT_NODE,
          blocksPersistence: true,
          enabledModes: STRUCTURAL_VALIDATION_MODES,
        },
        outputNode,
      };
    }
  }
  return {error: null, outputNode};
}

// This error should not be possible when using the UI
export function ensureEdgeConnectsNodesInFlowchart(
  edge: ProcessEdgeForValidation,
  flowchart: ProcessFlowchartForValidation
): MaybeValidationError {
  return !edgeConnectsNodesInFlowchart(edge, flowchart)
    ? getValidationErrorForEdge(
        flowchart,
        edge,
        () =>
          `Path '${getEdgeDisplayName(
            edge,
            flowchart
          )}' connects nodes that haven't been added to the flowchart.`,
        ProcessFlowchartErrorType.EDGE_CONNECTS_UNAVAILABLE_NODES
      )
    : null;
}

export function ensureEdgeBetweenSchemasIsValid(
  fromNodeSchemaId: string,
  toNodeSchemaId: string,
  designSpace: ProcessFlowchartDesignSpace
): boolean {
  return designSpace.availableEdges.some((tEdge) => {
    const fromTemplateNode = getTemplateNodeFromClientId(designSpace.availableNodes, tEdge.fromNodeId);
    const toTemplateNode = getTemplateNodeFromClientId(designSpace.availableNodes, tEdge.toNodeId);

    return fromNodeSchemaId === fromTemplateNode?.schemaId && toNodeSchemaId === toTemplateNode?.schemaId;
  });
}

interface EnsureControlFlowEdgeIsValidParams {
  edge: ProcessEdgeForValidation;
  flowchart: ProcessFlowchartForValidation;
  designSpace: ProcessFlowchartDesignSpace;
  usingFlowchartFirstConfig?: boolean;
}

export function ensureControlFlowEdgeIsValid(
  params: EnsureControlFlowEdgeIsValidParams
): Array<ProcessFlowchartValidationError> | ProcessFlowchartValidationError | null {
  const {edge, flowchart, designSpace, usingFlowchartFirstConfig} = params;

  if (edge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
    return null;
  }

  const genericEdgeError = (): ProcessFlowchartValidationError =>
    getValidationErrorForEdge(
      flowchart,
      edge,
      () => `Path '${getEdgeDisplayName(edge, flowchart)}' is not available for this flowchart.`,
      ProcessFlowchartErrorType.EDGE_NOT_AVAILABLE
    );

  // We have to get the schemas for the target edge. This requires a few hops to connect the client IDs.
  const fromNode = getFlowchartNodeFromClientId(flowchart, edge.source);
  const toNode = getFlowchartNodeFromClientId(flowchart, edge.target);
  if (fromNode == null || toNode == null) {
    return genericEdgeError();
  }
  if (toNode.nodeType === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS) {
    // Any node can have an edge to a router except for the flowchart output node.
    // But we should disable that via the UI
    return fromNode.nodeType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
      ? genericEdgeError()
      : null;
  }

  if (toNode.nodeType === ProcessFlowchartNodeDetailsType.END_NODE_DETAILS) {
    const inboundEdges = getInboundEdges(toNode, flowchart.edges);
    const outboundEdges = getOutboundEdges(toNode, flowchart.edges);
    if (fromNode.nodeType !== ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS) {
      return {
        object: {
          clientId: toNode.id,
          type: ProcessFlowchartObjectType.NODE,
        },
        errorMessage: 'Connect this stop to one router as its source.',
        errorType: ProcessFlowchartErrorType.END_NODE_CONNECTED_TO_NON_ROUTER_NODE,
        blocksPersistence: true,
      };
    } else if (inboundEdges.length > 1) {
      return {
        object: {
          clientId: toNode.id,
          type: ProcessFlowchartObjectType.NODE,
        },
        errorMessage: 'Connect this stop to one router as its source.',
        errorType: ProcessFlowchartErrorType.END_NODE_CONNECTED_TO_MULTIPLE_SOURCE_NODES,
        blocksPersistence: true,
      };
    } else if (outboundEdges.length > 0) {
      return {
        object: {
          clientId: toNode.id,
          type: ProcessFlowchartObjectType.NODE,
        },
        errorMessage: 'An end node cannot have outgoing edges.',
        errorType: ProcessFlowchartErrorType.END_NODE_HAS_OUTBOUND_EDGES,
        blocksPersistence: true,
      };
    }
    return null;
  }
  if (fromNode.nodeType === ProcessFlowchartNodeDetailsType.END_NODE_DETAILS) {
    return genericEdgeError();
  }

  if (usingFlowchartFirstConfig) {
    return null;
  } else {
    // TODO: Remove this once we have migrated to the new flowchart config editor
    // If the edge is from a router, we use the previous nodes to check that the edge is in the design space.
    // eslint-disable-next-line no-lonely-if
    if (fromNode.nodeType === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS) {
      const taskNodesBeforeRouter = getTaskNodesUpstreamOfNode(fromNode.id, flowchart);
      for (const taskNode of taskNodesBeforeRouter) {
        assert(
          taskNode.nodeType === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS ||
            taskNode.nodeType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS,
          'Task nodes upstream of a router node must be task or root nodes'
        );
        if (!ensureEdgeBetweenSchemasIsValid(taskNode.taskSchemaId, toNode.taskSchemaId, designSpace)) {
          return [
            getValidationErrorForEdge(
              flowchart,
              edge,
              () => `The transition from ${taskNode.name} to ${toNode.name} is not available`,
              ProcessFlowchartErrorType.EDGE_NOT_AVAILABLE
            ),
            {
              object: {
                clientId: fromNode.id,
                type: ProcessFlowchartObjectType.NODE,
              },
              errorMessage: `One or more transitions through this router are not configured.`,
              errorType: ProcessFlowchartErrorType.ROUTER_EDGES_NOT_CONFIGURED,
              blocksPersistence: true,
            },
          ];
        }
      }
    } else if (!ensureEdgeBetweenSchemasIsValid(fromNode.taskSchemaId, toNode.taskSchemaId, designSpace)) {
      return genericEdgeError();
    }
  }

  return null;
}

// This represents a technical problem. The UI should of course prevent this error from occurring, but an
// automation in the future may produce duplicate edges.
export function ensureNoDuplicateEdges(
  edge: ProcessEdgeForValidation,
  flowchart: ProcessFlowchartForValidation
): Array<ProcessFlowchartValidationError> | null {
  const errors: Array<ProcessFlowchartValidationError> = [];
  for (const scanEdge of flowchart.edges) {
    const bothEdgesAreControlFlow =
      edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW &&
      scanEdge.type === ProcessFlowchartEdgeType.CONTROL_FLOW;
    const bothEdgesAreDataFlow =
      edge.type === ProcessFlowchartEdgeType.DATA_FLOW &&
      scanEdge.type === ProcessFlowchartEdgeType.DATA_FLOW;
    const edgesHaveSameSourceAndTargetNodes =
      edge.source === scanEdge.source && edge.target === scanEdge.target;
    const isDuplicateControlFlowEdge = bothEdgesAreControlFlow && edgesHaveSameSourceAndTargetNodes;
    const isDuplicateDataFlowEdge =
      bothEdgesAreDataFlow &&
      edgesHaveSameSourceAndTargetNodes &&
      edge.sourceHandle === scanEdge.sourceHandle &&
      edge.targetHandle === scanEdge.targetHandle;

    if (edge.id !== scanEdge.id && (isDuplicateControlFlowEdge || isDuplicateDataFlowEdge)) {
      errors.push(
        getValidationErrorForEdge(
          flowchart,
          edge,
          () => `Path '${getEdgeDisplayName(edge, flowchart)}' is a duplicate.`,
          ProcessFlowchartErrorType.DUPLICATE_EDGES
        )
      );
    }
  }
  return errors.length > 0 ? errors : null;
}

/**
 * This validation checks that for every loop in a flowchart, you can only "enter" at one distinct node.
 * For instance, in the following graph:
 *       2 -->
 *     /      \
 *    1        5 -> R1 -> 7
 *     \      /      \
 *      3 -> 4        6
 *       \           /
 *         <---------
 * there exists a loop (3 -> 4 -> 5 -> R1 -> 6 -> 3) with two entry points (node 3, via 1 -> 3, and node 5,
 * via 2 -> 5), which is disallowed.
 *
 * We disallow this because it complicates ancestor-finding. Because we track ancestry using a DFS that
 * stops when we detect a cycle, in the above graph, we might detect an ancestor of 3 that we would otherwise
 * ignore.
 *
 * If we traverse the path 1 -> 2 -> 5 -> R1 -> 6 -> 3, we set 5 as an ancestor of 3, because no cycle
 * has been detected. However, if we traverse the path 1 -> 3 -> 4 -> 5 -> R1 -> 6 -> 3, we set 3 as an
 * ancestor of 5, and seeing a loop, do not set 5 as an ancestor of 3. For LCA purposes, this would be correct.
 */
export function ensureLoopsHaveOneEntryPoint(
  flowchart: ProcessFlowchartForValidation
): Array<ProcessFlowchartValidationError> | null {
  const rootNode = flowchart.nodes.find(
    (node) => node.nodeType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
  );
  if (rootNode == null) {
    return null;
  }
  const adjacencyMap: AdjacencyMap = getAdjacencyMapFromProcessFlowchart(flowchart);
  const {nodeIdsToAncestors} = getLoopIgnoringAncestorsAndEdges(rootNode.id, adjacencyMap);

  const nodesInLoop: Set<string> = new Set();
  // We iterate through each node's ancestors.
  // If node A has an ancestor, node B, who has also detected node A as its ancestor, we error on both node A and node B.
  for (const [nodeId, ancestors] of Object.entries(nodeIdsToAncestors)) {
    if (nodesInLoop.has(nodeId)) {
      // Stop early if we're already erroring on the node
      continue;
    }
    for (const ancestor of ancestors) {
      if (ancestor !== nodeId && nodeIdsToAncestors[ancestor].has(nodeId)) {
        // We ignore the ancestor when it is the node itself
        nodesInLoop.add(nodeId);
        nodesInLoop.add(ancestor);
        break;
      }
    }
  }

  return [...nodesInLoop].map((nodeId) => ({
    object: {
      clientId: nodeId,
      type: ProcessFlowchartObjectType.NODE,
    },
    errorMessage: `Each loop can only have one starting point.`,
    errorType: ProcessFlowchartErrorType.LOOP_HAS_MULTIPLE_ENTRY_POINTS,
    blocksPersistence: true,
  }));
}

export function ensureLoopsContainTaskAndRouterNode(
  node: ProcessNodeForValidation,
  flowchart: ProcessFlowchartForValidation
): MaybeValidationError {
  const rootNode = flowchart.nodes.find(
    (node) => node.nodeType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
  );
  if (rootNode == null) {
    return null;
  }

  const adjacencyMap: AdjacencyMap = getAdjacencyMapFromProcessFlowchart(flowchart);

  const nodesByClientId = getNodesByClientId(flowchart);
  const cycles = detectCyclesWithNode(adjacencyMap, node.id);
  for (const cyclicNodes of cycles) {
    const hasRouterNode = Array.from(cyclicNodes).some(
      (clientId) => nodesByClientId[clientId].nodeType === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS
    );
    const hasTaskNode = Array.from(cyclicNodes).some(
      (clientId) => nodesByClientId[clientId].nodeType === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS
    );
    if (!hasRouterNode || !hasTaskNode) {
      return {
        object: {
          clientId: node.id,
          type: ProcessFlowchartObjectType.NODE,
        },
        errorMessage: `Each loop must include at least one task schema and one router.`,
        errorType: ProcessFlowchartErrorType.LOOP_MISSING_TASK_OR_ROUTER_NODE,
        blocksPersistence: true,
        enabledModes: STRUCTURAL_VALIDATION_MODES,
      };
    }
  }
  return null;
}

export function ensureFlowchartDoesNotExceedMaxNumberOfNodes(
  flowchart: ProcessFlowchartForValidation
): MaybeValidationError {
  const rootNode = flowchart.nodes.find(
    (node) => node.nodeType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
  );
  if (rootNode == null) {
    return null;
  }
  const numNodes = flowchart.nodes.length;
  return numNodes > BenchlingConfig.WORKFLOWS_MAX_NODES_PER_FLOWCHART_CAN_BE_RENDERED_IN_UI
    ? {
        object: {
          clientId: flowchart.id,
          type: ProcessFlowchartObjectType.FLOWCHART_CONFIG,
        },
        errorMessage: `This flowchart has ${numNodes} blocks, which exceeds the limit of ${BenchlingConfig.WORKFLOWS_MAX_NODES_PER_FLOWCHART_CAN_BE_RENDERED_IN_UI}.`,
        errorType: ProcessFlowchartErrorType.MAX_NODES_LIMIT_EXCEEDED,
        blocksPersistence: true,
      }
    : null;
}

interface EnsureNodeNameIsNotEmptyParams {
  node: ProcessNodeForValidation;
  usingFlowchartFirstConfig: boolean;
}

// This will be self-healed so we should never encounter this error in real life
export function ensureNodeNameIsNotEmpty(params: EnsureNodeNameIsNotEmptyParams): MaybeValidationError {
  const {node, usingFlowchartFirstConfig} = params;
  if (node.name != null && node.name.length === 0) {
    return {
      object: {
        clientId: node.id,
        type: ProcessFlowchartObjectType.NODE,
      },
      errorMessage: `Enter a name for this ${getDisplayNameForNode(node, usingFlowchartFirstConfig)}.`,
      errorType: ProcessFlowchartErrorType.UNNAMED_NODE,
      blocksPersistence: true,
    };
  }
  return null;
}

export function ensureDataFlowEdgeHasAssociatedControlFlowEdge(
  edge: ProcessEdgeForValidation,
  flowchart: ProcessFlowchartForValidation
): Array<ProcessFlowchartValidationError> | null {
  assert(edge.type === ProcessFlowchartEdgeType.DATA_FLOW, 'Expected data flow edge');
  // Two cases are valid, first if the data flow edge connects two nodes that are directly connected by a control flow edge.
  if (hasMatchingControlFlowEdge(edge, flowchart)) {
    return null;
  }
  // Second, if the data flow edge connects two nodes that are connected by a router node.
  if (getTaskNodesDownstreamOfNode(edge.source, flowchart).some((node) => node.id === edge.target)) {
    return null;
  }
  const targetNode = getFlowchartNodeFromClientId(flowchart, edge.target);
  assert(targetNode != null, 'Expected target node');
  // Otherwise, we error.
  return [
    // Error to be displayed on the target node
    {
      object: {
        clientId: targetNode.id,
        type: ProcessFlowchartObjectType.NODE,
      },
      errorMessage: 'Field mappings can only flow between connected nodes.',
      errorType: ProcessFlowchartErrorType.DATA_FLOW_EDGE_WITH_NO_CONTROL_FLOW_EDGE,
      blocksPersistence: true,
      severity: FlowchartValidationSeverity.WARNING,
      enabledModes: DATA_FLOW_VALIDATION_MODES,
    },
    // Error to be displayed on the data flow edge
    {
      object: {
        clientId: edge.id,
        type: ProcessFlowchartObjectType.EDGE,
      },
      errorMessage: 'Field mappings can only flow between connected nodes.',
      errorType: ProcessFlowchartErrorType.DATA_FLOW_EDGE_WITH_NO_CONTROL_FLOW_EDGE,
      blocksPersistence: true,
      severity: FlowchartValidationSeverity.WARNING,
      enabledModes: DATA_FLOW_VALIDATION_MODES,
    },
  ];
}

interface EnsureDataFlowEdgeSourceIsValidParams {
  edge: ProcessEdgeForValidation;
  flowchart: ProcessFlowchartForValidation;
  usingFlowchartFirstConfig: boolean;
}

export function ensureDataFlowEdgeSourceIsValid(
  params: EnsureDataFlowEdgeSourceIsValidParams
): Array<ProcessFlowchartValidationError> | null {
  const {edge, flowchart, usingFlowchartFirstConfig} = params;
  assert(edge.type === ProcessFlowchartEdgeType.DATA_FLOW, 'Expected data flow edge');
  assert(edge.sourceHandle != null && edge.targetHandle != null, 'Expected source and target handles');
  const edgeTransitionSource = edge.transitionSource;
  if (edgeTransitionSource == null) {
    return null;
  }
  const sourceFieldInfo = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
  const allowedSectionTypes = [TaskSchemaNodeSectionType.INPUTS, TaskSchemaNodeSectionType.MAP_FROM_START];
  if (
    edgeTransitionSource === TransitionSource.INPUTS &&
    !allowedSectionTypes.includes(sourceFieldInfo.sectionType)
  ) {
    const sourceNode = getFlowchartNodeFromClientId(flowchart, edge.source);
    assert(sourceNode != null, 'Expected source node');
    return [
      {
        object: {
          clientId: edge.target,
          type: ProcessFlowchartObjectType.NODE,
        },
        parentObjects: [
          {
            clientId: edge.id,
            type: ProcessFlowchartObjectType.EDGE,
          },
        ],
        errorMessage: `This ${getDisplayNameForNode(sourceNode, usingFlowchartFirstConfig)}'s transition behavior doesn't match its field mappings. Update the transition behavior or adjust the field mappings.`,
        errorType: ProcessFlowchartErrorType.DATA_FLOW_EDGE_SOURCE_TYPE_MISMATCH,
        blocksPersistence: true,
        enabledModes: DATA_FLOW_VALIDATION_MODES,
      },
    ];
  }
  return null;
}

/**
 * Returns the set of edge IDs that conflict because they come from different source PTDVs
 * within the same transition, all targeting the same field.
 */
function getConflictingEdgeIdsAcrossTransitionSources(
  edgesForTargetField: Array<DataFlowEdgeForValidation>,
  nodeIdToPTDVId: Map<string, string>,
  transitionsByTargetBySourcePTDVs: Record<string, Record<string, Array<string>>>
): Set<string> {
  const targetPTDVId = nodeIdToPTDVId.get(edgesForTargetField[0].target)!;

  // Group by transition -> source -> edges
  const edgesByTransition: Record<string, Record<string, Array<string>>> = {};
  for (const edge of edgesForTargetField) {
    const sourcePTDVId = nodeIdToPTDVId.get(edge.source)!;
    const transitionsForSourcePTDV = transitionsByTargetBySourcePTDVs[targetPTDVId]?.[sourcePTDVId];
    for (const tId of transitionsForSourcePTDV ?? []) {
      ((edgesByTransition[tId] ??= {})[sourcePTDVId] ??= []).push(edge.id);
    }
  }

  // Filter for transitions with multiple distinct sources and collected the edge ids
  const conflictingEdgeIds = new Set<string>();
  for (const edgesBySourcePTDV of Object.values(edgesByTransition)) {
    if (Object.keys(edgesBySourcePTDV).length > 1) {
      for (const edgeIds of Object.values(edgesBySourcePTDV)) {
        for (const edgeId of edgeIds) {
          conflictingEdgeIds.add(edgeId);
        }
      }
    }
  }
  return conflictingEdgeIds;
}

/**
 * Validates that data flow edges do not conflict on a target field.
 *
 * In all modes: a single source node cannot have multiple data flow edges mapping to the same
 * target field on the same target node.  Multiple source nodes in the same transition cannot have
 * data flow edges mapping to the same target field on the same target node
 *
 * In single-transition mode: different source nodes (with different PTDVs) cannot
 * have data flow edges mapping to the same target field, because this would create conflicting
 * mapper source configs on the backend. We only check this in single-transition mode because it's
 * only applicable to merging nodes.
 */
export function ensureDataFlowEdgesDoNotConflictOnTargetField(
  flowchart: ProcessFlowchartForValidation,
  validationContext: ValidationContext
): Array<ProcessFlowchartValidationError> {
  const dataFlowEdges = flowchart.edges.filter(
    (edge): edge is DataFlowEdgeForValidation => edge.type === ProcessFlowchartEdgeType.DATA_FLOW
  );

  const nodeIdToPTDVId = buildNodeIdToPTDVIdMap(flowchart.nodes);

  // Group edges by (target node, target handle) to find all edges targeting the same field
  const edgesByTargetField = new Map<string, Array<DataFlowEdgeForValidation>>();
  for (const edge of dataFlowEdges) {
    const key = `${edge.target}:${edge.targetHandle}`;
    if (!edgesByTargetField.has(key)) {
      edgesByTargetField.set(key, []);
    }
    edgesByTargetField.get(key)!.push(edge);
  }
  // Record<targetPTDVId, Record<sourcePTDVId, Array<transitionId>>>
  const transitionsByTargetBySourcePTDVs: Record<string, Record<string, Array<string>>> = {};
  for (const transition of Object.values(validationContext.transitions)) {
    for (const sourcePTDVId of transition.sourcePTDVIds) {
      ((transitionsByTargetBySourcePTDVs[transition.targetPTDVId] ??= {})[sourcePTDVId] ??= []).push(
        transition.id
      );
    }
  }

  const errors: Array<ProcessFlowchartValidationError> = [];

  for (const [, edgesForTargetField] of edgesByTargetField) {
    if (edgesForTargetField.length <= 1) {
      continue;
    }

    // Check 1 (all modes): collect edges that conflict on the same target field, either
    // because the same source node maps twice, or because different PTDVs within the same
    // transition both target it.
    const conflictingEdgeIds = new Set<string>();

    // Same source node maps to the same target field multiple times
    const seenSources = new Set<string>();
    const duplicateSources = new Set<string>();
    for (const edge of edgesForTargetField) {
      if (seenSources.has(edge.source)) {
        duplicateSources.add(edge.source);
      }
      seenSources.add(edge.source);
    }
    for (const edge of edgesForTargetField) {
      if (duplicateSources.has(edge.source)) {
        conflictingEdgeIds.add(edge.id);
      }
    }

    // Different source PTDVs from the same merging transition target the same field
    for (const edgeId of getConflictingEdgeIdsAcrossTransitionSources(
      edgesForTargetField,
      nodeIdToPTDVId,
      transitionsByTargetBySourcePTDVs
    )) {
      conflictingEdgeIds.add(edgeId);
    }

    if (conflictingEdgeIds.size > 0) {
      for (const edgeId of conflictingEdgeIds) {
        errors.push({
          object: {clientId: edgeId, type: ProcessFlowchartObjectType.EDGE},
          errorMessage: '',
          errorType: ProcessFlowchartErrorType.DATA_FLOW_EDGE_TARGET_FIELD_COLLISION,
          blocksPersistence: true,
          enabledModes: DATA_FLOW_VALIDATION_MODES,
        });
      }
      errors.push({
        object: {clientId: edgesForTargetField[0].target, type: ProcessFlowchartObjectType.NODE},
        errorMessage: 'Transition cannot target the same field multiple times.',
        errorType: ProcessFlowchartErrorType.DATA_FLOW_EDGE_TARGET_FIELD_COLLISION,
        blocksPersistence: true,
        enabledModes: DATA_FLOW_VALIDATION_MODES,
      });
    }

    // Check 2 (single-transition mode only): different PTDVs map to the same target field
    const sourcePTDVs = new Set<string>();
    for (const edge of edgesForTargetField) {
      const ptdvId = nodeIdToPTDVId.get(edge.source);
      if (ptdvId != null) {
        sourcePTDVs.add(ptdvId);
      }
    }

    if (sourcePTDVs.size > 1) {
      for (const edge of edgesForTargetField) {
        errors.push({
          object: {clientId: edge.id, type: ProcessFlowchartObjectType.EDGE},
          errorMessage: '',
          errorType: ProcessFlowchartErrorType.DATA_FLOW_EDGE_TARGET_FIELD_COLLISION,
          blocksPersistence: true,
          enabledModes: [FlowchartEditorMode.SINGLE_TRANSITION_CANVAS],
        });
      }
      errors.push({
        object: {clientId: edgesForTargetField[0].target, type: ProcessFlowchartObjectType.NODE},
        errorMessage:
          "Multiple source task types can't map to the same target field. Remove a source node or use a different target field.",
        errorType: ProcessFlowchartErrorType.DATA_FLOW_EDGE_TARGET_FIELD_COLLISION,
        blocksPersistence: true,
        enabledModes: [FlowchartEditorMode.SINGLE_TRANSITION_CANVAS],
      });
    }
  }

  return errors;
}

/**
 * Validates that no transition is a subset or superset of another transition targeting the same PTDV.
 * E.g. having both {a, b} -> c and {a, b, d} -> c is invalid because {a, b} is a subset of {a, b, d}.
 *
 * On the main canvas, planned transitions are checked against ALL other transitions (both planned
 * and optional). Errors are reported on the control flow edges and destination node of both subset
 * and superset planned transitions.
 *
 * On the single transition canvas, the current transition (derived from the flowchart's edges) is
 * checked for subset/superset relationships against all other transitions. The flowchart ID is used
 * to identify and exclude the current transition from the comparison set.
 */
export function ensureTransitionsAreNotSubsetOfConfigured(
  flowchart: ProcessFlowchartForValidation,
  validationContext: ValidationContext
): Array<ProcessFlowchartValidationError> | null {
  const TRANSITION_SUBSET_ERROR_MESSAGE_MAIN =
    'This transition is a subset of another configured transition. Remove it or merge with the other.';
  const TRANSITION_SUPERSET_ERROR_MESSAGE_MAIN =
    'This transition is a superset of another configured transition. Remove it or merge with the other.';
  const TRANSITION_SUBSET_ERROR_MESSAGE_SINGLE =
    'This transition is a subset of an existing transition. Remove it or merge it with the existing one.';
  const TRANSITION_SUPERSET_ERROR_MESSAGE_SINGLE =
    'This transition is a superset of an existing transition. Remove it or merge it with the existing one.';

  const {transitions, editorMode} = validationContext;
  if (transitions == null || Object.keys(transitions).length === 0) {
    return null;
  }

  const isSingleTransitionCanvas = editorMode === FlowchartEditorMode.SINGLE_TRANSITION_CANVAS;

  let transitionsToCheck: Record<string, Transition>;
  let currentTransitionId: string | null = null;

  // Build list of transitions to check based on the editor mode.
  if (isSingleTransitionCanvas) {
    const transitionTargetsAndSources = getTransitionTargetsAndSources(flowchart);
    if (transitionTargetsAndSources.length !== 1) {
      return null;
    }
    const current = transitionTargetsAndSources[0];
    // In the single transition canvas, the flowchart ID is the ID of the transition being configured.
    currentTransitionId = flowchart.id;
    const derivedTransition: Transition = {
      id: currentTransitionId,
      sourcePTDVIds: current.sourcePTDVIds,
      targetPTDVId: current.targetPTDVId,
      transitionSourcesBySourcePTDVId: {},
      customMappingDataByFieldPair: {},
      isPlanned: false,
    };
    transitionsToCheck = {...transitions, [currentTransitionId]: derivedTransition};
  } else {
    transitionsToCheck = transitions;
  }

  // Check for subset and superset relationships within transitions
  const {subsetTransitionIds, supersetTransitionIds} =
    buildTransitionIndexByPTDVPairWithSubsets(transitionsToCheck);
  if (subsetTransitionIds.length === 0 && supersetTransitionIds.length === 0) {
    return null;
  }

  const nodeIdToPTDVId = buildNodeIdToPTDVIdMap(flowchart.nodes);

  // Pre-compute incoming control flow edges per node
  const incomingControlFlowEdgesByNodeId = new Map<string, Array<ProcessEdgeForValidation>>();
  for (const edge of flowchart.edges) {
    if (edge.type !== ProcessFlowchartEdgeType.CONTROL_FLOW) {
      continue;
    }
    if (!incomingControlFlowEdgesByNodeId.has(edge.target)) {
      incomingControlFlowEdgesByNodeId.set(edge.target, []);
    }
    incomingControlFlowEdgesByNodeId.get(edge.target)!.push(edge);
  }

  const errors: Array<ProcessFlowchartValidationError> = [];
  const erroredNodeIds = new Set<string>();

  const subsetErrorMessage = isSingleTransitionCanvas
    ? TRANSITION_SUBSET_ERROR_MESSAGE_SINGLE
    : TRANSITION_SUBSET_ERROR_MESSAGE_MAIN;
  const supersetErrorMessage = isSingleTransitionCanvas
    ? TRANSITION_SUPERSET_ERROR_MESSAGE_SINGLE
    : TRANSITION_SUPERSET_ERROR_MESSAGE_MAIN;

  const flaggedTransitions: Array<{
    transitionIds: Array<string>;
    errorMessage: string;
    errorType: ProcessFlowchartErrorType;
  }> = [
    {
      transitionIds: subsetTransitionIds,
      errorMessage: subsetErrorMessage,
      errorType: ProcessFlowchartErrorType.TRANSITION_IS_SUBSET_OF_CONFIGURED,
    },
    {
      transitionIds: supersetTransitionIds,
      errorMessage: supersetErrorMessage,
      errorType: ProcessFlowchartErrorType.TRANSITION_IS_SUPERSET_OF_CONFIGURED,
    },
  ];

  // Add errors to nodes / edges for invalid transitions
  for (const {transitionIds, errorMessage, errorType} of flaggedTransitions) {
    for (const transitionId of transitionIds) {
      const transition = transitionsToCheck[transitionId];
      if (transition == null) {
        continue;
      }

      if (isSingleTransitionCanvas) {
        if (transitionId !== currentTransitionId) {
          continue;
        }
      } else if (!transition.isPlanned) {
        continue;
      }

      const sourcePTDVIdSet = new Set(transition.sourcePTDVIds);
      const targetPTDVId = transition.targetPTDVId;
      const enabledModes = [FlowchartEditorMode.MAIN_CANVAS, FlowchartEditorMode.SINGLE_TRANSITION_CANVAS];
      const baseError = {
        errorMessage,
        errorType,
        blocksPersistence: true,
        enabledModes,
      };

      // Find nodes whose PTDV matches the transition's target, then verify
      // the exact transition is present by checking incoming edges.
      for (const node of flowchart.nodes) {
        const nodePTDV = nodeIdToPTDVId.get(node.id);
        if (nodePTDV !== targetPTDVId) {
          continue;
        }

        const incomingEdges = incomingControlFlowEdgesByNodeId.get(node.id) ?? [];
        const incomingEdgesAndUpstreamPTDVs = incomingEdges.map((edge) => ({
          edge,
          sourcePTDVs: getSourcePTDVsUpstreamOfControlFlowEdge(
            edge,
            flowchart,
            nodeIdToPTDVId,
            sourcePTDVIdSet
          ),
        }));
        const matchingEdgesAndUpstreamPTDVs = incomingEdgesAndUpstreamPTDVs.filter(
          ({sourcePTDVs}) => sourcePTDVs.size > 0
        );

        // Verify all of the transition's source PTDVs are covered by incoming edges (including via routers)
        const coveredSourcePTDVs = new Set<string>();
        for (const {sourcePTDVs} of matchingEdgesAndUpstreamPTDVs) {
          for (const ptdv of sourcePTDVs) {
            coveredSourcePTDVs.add(ptdv);
          }
        }
        if (![...sourcePTDVIdSet].every((ptdv) => coveredSourcePTDVs.has(ptdv))) {
          continue;
        }

        for (const {edge} of matchingEdgesAndUpstreamPTDVs) {
          errors.push({
            ...baseError,
            object: {
              clientId: edge.id,
              type: ProcessFlowchartObjectType.EDGE,
            },
          });
        }

        if (!erroredNodeIds.has(node.id)) {
          erroredNodeIds.add(node.id);
          errors.push({
            ...baseError,
            object: {
              clientId: node.id,
              type: ProcessFlowchartObjectType.NODE,
            },
          });
        }
      }
    }
  }

  return errors.length > 0 ? errors : null;
}

/**
 * Validates that partially-overlapping transitions (sharing at least one source PTDV but neither
 * being a subset/superset of the other) do not target the same canvas node.
 *
 * For each target node, finds which transitions are fulfilled there (all sources present
 * upstream), then checks if any pair of those partially overlaps. If so, the user needs to use
 * separate destination nodes.
 */
export function ensureOverlappingTransitionsHaveSeparateTargetNodes(
  flowchart: ProcessFlowchartForValidation,
  validationContext: ValidationContext
): Array<ProcessFlowchartValidationError> | null {
  const {transitions, editorMode} = validationContext;
  if (Object.keys(transitions).length === 0 || editorMode === FlowchartEditorMode.SINGLE_TRANSITION_CANVAS) {
    return null;
  }

  const nodeIdToPTDVId = buildNodeIdToPTDVIdMap(flowchart.nodes);

  // Group transitions by target PTDV
  const transitionsByTarget: Record<string, Array<Transition>> = {};
  for (const transition of Object.values(transitions)) {
    (transitionsByTarget[transition.targetPTDVId] ??= []).push(transition);
  }

  const errors: Array<ProcessFlowchartValidationError> = [];

  for (const node of flowchart.nodes) {
    const nodePTDV = nodeIdToPTDVId.get(node.id);
    if (nodePTDV == null) {
      continue;
    }
    const candidateTransitions = transitionsByTarget[nodePTDV];
    if (candidateTransitions == null || candidateTransitions.length < 2) {
      continue;
    }

    // Gather all upstream PTDVs reachable via incoming control flow edges
    const incomingEdges = flowchart.edges.filter(
      (e) => e.target === node.id && e.type === ProcessFlowchartEdgeType.CONTROL_FLOW
    );
    // Used for showing errors on edges later
    const edgeToUpstreamPTDVs: Record<string, Set<string>> = {};
    const upstreamPTDVs = new Set<string>();
    for (const edge of incomingEdges) {
      const ptdvs = getSourcePTDVsUpstreamOfControlFlowEdge(
        edge,
        flowchart,
        nodeIdToPTDVId,
        new Set(candidateTransitions.flatMap((t) => t.sourcePTDVIds))
      );
      edgeToUpstreamPTDVs[edge.id] = ptdvs;
      for (const ptdv of ptdvs) {
        upstreamPTDVs.add(ptdv);
      }
    }

    // Find which transitions are fulfilled on this node
    const fulfilledTransitions = candidateTransitions.filter((t) =>
      t.sourcePTDVIds.every((src) => upstreamPTDVs.has(src))
    );

    // Exclude transitions that are in a subset/superset relationship — those are
    // already caught by ensureTransitionsAreNotSubsetOfConfigured.
    const nonSubsetFulfilledTransitions = fulfilledTransitions.filter(
      (t) =>
        !fulfilledTransitions.some(
          (other) => other.id !== t.id && t.sourcePTDVIds.every((src) => other.sourcePTDVIds.includes(src))
        )
    );

    if (nonSubsetFulfilledTransitions.length < 2) {
      continue;
    }

    // Check if the fulfilled transitions overlap
    const sourceCounts: Record<string, Array<Transition>> = {};
    for (const transition of nonSubsetFulfilledTransitions) {
      for (const src of transition.sourcePTDVIds) {
        (sourceCounts[src] ??= []).push(transition);
      }
    }

    const sharedSources = new Set(
      Object.entries(sourceCounts)
        .filter(([_, ts]) => ts.length > 1)
        .map(([src, _]) => src)
    );
    if (sharedSources.size === 0) {
      continue;
    }

    for (const edge of incomingEdges) {
      if ([...edgeToUpstreamPTDVs[edge.id]].some((ptdv) => sharedSources.has(ptdv))) {
        errors.push({
          errorMessage:
            'Overlapping transitions cannot target the same node. Use separate destination nodes for each transition.',
          errorType: ProcessFlowchartErrorType.OVERLAPPING_TRANSITIONS_ON_SAME_NODE,
          blocksPersistence: true,
          enabledModes: STRUCTURAL_VALIDATION_MODES,
          object: {
            clientId: edge.id,
            type: ProcessFlowchartObjectType.EDGE,
          },
        });
      }
    }
    errors.push({
      errorMessage:
        'Overlapping transitions cannot target the same node. Use separate destination nodes for each transition.',
      errorType: ProcessFlowchartErrorType.OVERLAPPING_TRANSITIONS_ON_SAME_NODE,
      blocksPersistence: true,
      enabledModes: STRUCTURAL_VALIDATION_MODES,
      object: {
        clientId: node.id,
        type: ProcessFlowchartObjectType.NODE,
      },
    });
  }

  return errors.length > 0 ? errors : null;
}

/**
 * Legacy-editor equivalent of `ensureOverlappingTransitionsHaveSeparateTargetNodes`.
 * Operates on design-space mapper configs rather than transitions.
 *
 * For each task/output node, finds which inbound mapper configs are individually fulfilled
 * (all their sources are upstream), then checks if any pair partially overlaps
 * (shared source, neither subset/superset).
 */
export function ensureOverlappingMapperConfigsHaveSeparateTargetNodes(
  flowchart: ProcessFlowchartForValidation,
  designSpace: ProcessFlowchartDesignSpace,
  usingFlowchartFirstConfig?: boolean
): Array<ProcessFlowchartValidationError> | null {
  if (usingFlowchartFirstConfig) {
    return null;
  }
  const errors: Array<ProcessFlowchartValidationError> = [];

  for (const node of flowchart.nodes) {
    if (
      node.nodeType !== ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS &&
      node.nodeType !== ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
    ) {
      continue;
    }
    const templateNode = getTemplateNodeFromSchemaId(designSpace.availableNodes, node.taskSchemaId);
    if (templateNode == null || templateNode.inboundMapperConfigs.length < 2) {
      continue;
    }

    const upstreamSchemaIds = new Set(
      compact(
        getTaskNodesUpstreamOfNode(node.id, flowchart).map((upstreamNode) =>
          getSchemaIdFromNodeClientId(flowchart, upstreamNode.id)
        )
      )
    );

    const fulfilled = templateNode.inboundMapperConfigs.filter((config) =>
      config.fromTaskSchemaIds.every((id) => upstreamSchemaIds.has(id))
    );
    if (fulfilled.length < 2) {
      continue;
    }

    const hasOverlap = fulfilled.some((a) =>
      fulfilled.some((b) => {
        if (a.clientId === b.clientId) {
          return false;
        }
        const aSet = new Set(a.fromTaskSchemaIds);
        const bSet = new Set(b.fromTaskSchemaIds);
        const hasShared = [...aSet].some((id) => bSet.has(id));
        return hasShared && !isSubset(aSet, bSet) && !isSubset(bSet, aSet);
      })
    );

    if (hasOverlap) {
      errors.push({
        object: {clientId: node.id, type: ProcessFlowchartObjectType.NODE},
        errorMessage:
          'Overlapping transitions cannot target the same node. Use separate destination nodes for each transition.',
        errorType: ProcessFlowchartErrorType.OVERLAPPING_TRANSITIONS_ON_SAME_NODE,
        blocksPersistence: true,
      });
    }
  }

  return errors.length > 0 ? errors : null;
}

interface TransitionTargetWithSources {
  targetPTDVId: string;
  targetNodeId: string;
  sourcePTDVIds: Array<string>;
}

/**
 * Returns the targets and their respective sources for a flowchart or transition. A target is a
 * task schema node with incoming control-flow edges from other task schema nodes.
 *
 * This assumes no router nodes are present for current use cases (in the single transitions modal).
 */
function getTransitionTargetsAndSources(
  flowchart: ProcessFlowchartForValidation
): Array<TransitionTargetWithSources> {
  const nodeIdToPTDVId = buildNodeIdToPTDVIdMap(flowchart.nodes);

  const transitionTargetsWithSources: Array<TransitionTargetWithSources> = [];

  for (const edge of flowchart.edges) {
    if (edge.type !== ProcessFlowchartEdgeType.CONTROL_FLOW) {
      continue;
    }
    if (nodeIdToPTDVId.has(edge.source) && nodeIdToPTDVId.has(edge.target)) {
      const sourcePTDVId = nodeIdToPTDVId.get(edge.source);
      const targetPTDVId = nodeIdToPTDVId.get(edge.target);
      if (sourcePTDVId != null && targetPTDVId != null) {
        const existingTargetWithSources = transitionTargetsWithSources.find(
          (t) => edge.target === t.targetNodeId
        );
        if (existingTargetWithSources != null) {
          existingTargetWithSources.sourcePTDVIds.push(sourcePTDVId);
        } else {
          transitionTargetsWithSources.push({
            targetPTDVId,
            targetNodeId: edge.target,
            sourcePTDVIds: [sourcePTDVId],
          });
        }
      }
    }
  }

  return transitionTargetsWithSources;
}

/**
 * Validates that no transition that is being configured duplicates another pre-existing transition (optional or planned).
 * In SINGLE_TRANSITION_CANVAS mode the flowchart parameter represents the
 * transition being configured, so we derive the target PTDV and source PTDVs from its
 * nodes and control-flow edges and check them against other transitions.
 *
 * Note that this only applies to optional transitions that are being drawn - if a planned transition is a duplicate of another planned transition,
 * it should be handled by mirroring, and if a planned transition is a duplicate of an optional transition,
 * that should be handled by auto-promotion.
 */
export function ensureTransitionIsNotDuplicate(
  flowchart: ProcessFlowchartForValidation,
  validationContext: ValidationContext
): Array<ProcessFlowchartValidationError> | null {
  const DUPLICATE_PLANNED_ERROR_MESSAGE = 'This transition already exists on the main canvas.';
  const DUPLICATE_OPTIONAL_ERROR_MESSAGE = 'This transition already exists as optional.';

  const {transitions} = validationContext;
  if (transitions == null || Object.keys(transitions).length === 0) {
    return null;
  }

  // flowchart.id is the ID of the transition being configured. We exclude it from the comparison.
  const otherTransitions = Object.values(transitions).filter((t) => t.id !== flowchart.id);
  if (otherTransitions.length === 0) {
    return null;
  }

  const transitionTargetsAndSources = getTransitionTargetsAndSources(flowchart);
  // In the single transition canvas, we assume at most one target node. Multiple targets will be handled by the ensureTransitionHasSingleTarget rule.
  if (transitionTargetsAndSources.length === 0 || transitionTargetsAndSources.length > 1) {
    return null;
  }
  const transition = transitionTargetsAndSources[0];
  const uniqueSourcePTDVIds = [...new Set(transition.sourcePTDVIds)];

  const isSameTransitionShape = (otherTransition: Transition): boolean =>
    transition.targetPTDVId === otherTransition.targetPTDVId &&
    uniqueSourcePTDVIds.length === otherTransition.sourcePTDVIds.length &&
    uniqueSourcePTDVIds.every((s) => otherTransition.sourcePTDVIds.includes(s));

  const duplicateTransitions = otherTransitions.filter((otherTransition) =>
    isSameTransitionShape(otherTransition)
  );
  const hasDuplicate = duplicateTransitions.length > 0;
  const duplicateOnMainCanvas = duplicateTransitions.some((t) => t.isPlanned);

  if (hasDuplicate) {
    const errorMessage = duplicateOnMainCanvas
      ? DUPLICATE_PLANNED_ERROR_MESSAGE
      : DUPLICATE_OPTIONAL_ERROR_MESSAGE;
    return [
      {
        errorMessage,
        errorType: ProcessFlowchartErrorType.TRANSITION_IS_DUPLICATE,
        blocksPersistence: true,
        enabledModes: [FlowchartEditorMode.SINGLE_TRANSITION_CANVAS],
        object: {
          clientId: flowchart.id,
          type: ProcessFlowchartObjectType.TRANSITION,
        },
      },
    ];
  }

  return null;
}

/**
 * In the single transition canvas, a transition must have exactly one target node.
 */
export function ensureTransitionHasSingleTarget(
  flowchart: ProcessFlowchartForValidation
): Array<ProcessFlowchartValidationError> | null {
  const transitionTargetsWithSources = getTransitionTargetsAndSources(flowchart);

  if (transitionTargetsWithSources.length > 1) {
    return transitionTargetsWithSources.map(({targetNodeId}) => ({
      errorMessage:
        'Only one connection is allowed in this transition. Remove extra connections so only one remains.',
      errorType: ProcessFlowchartErrorType.TRANSITION_HAS_MULTIPLE_TARGETS,
      blocksPersistence: true,
      enabledModes: [FlowchartEditorMode.SINGLE_TRANSITION_CANVAS],
      object: {
        clientId: targetNodeId,
        type: ProcessFlowchartObjectType.NODE,
      },
    }));
  }

  return null;
}

/**
 * Validates that merging transitions on the main canvas do not have multiple source nodes of the
 * same task type feeding into the target.
 *
 * E.g. if a merging transition is configured as A+B → C, drawing A1, A2, B1 → C is invalid
 * because there are two instances of source type A.
 */
export function ensureMergingTransitionSourcePTDVsAreNotDuplicated(
  flowchart: ProcessFlowchartForValidation,
  validationContext: ValidationContext
): Array<ProcessFlowchartValidationError> | null {
  const {transitions} = validationContext;

  // Index merging transitions by their target PTDV for more efficient lookup per node
  const mergingTransitionsByTargetPTDV: Record<string, Array<Transition>> = {};
  for (const transition of Object.values(transitions)) {
    if (transition.sourcePTDVIds.length > 1) {
      (mergingTransitionsByTargetPTDV[transition.targetPTDVId] ??= []).push(transition);
    }
  }

  if (Object.keys(mergingTransitionsByTargetPTDV).length === 0) {
    return null;
  }

  const nodeIdToPTDVId = buildNodeIdToPTDVIdMap(flowchart.nodes);
  const errors: Array<ProcessFlowchartValidationError> = [];

  for (const node of flowchart.nodes) {
    if (!isTaskSchemaNode(node)) {
      continue;
    }
    const nodePTDVId = node.taskDefinitionVersionSpec.clientId;
    const mergingTransitions = mergingTransitionsByTargetPTDV[nodePTDVId];
    if (mergingTransitions == null) {
      continue;
    }

    const incomingControlFlowEdges = getInboundEdges(node, flowchart.edges).filter(
      (edge) => edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW
    );

    for (const transition of mergingTransitions) {
      const sourcePTDVIdSet = new Set(transition.sourcePTDVIds);

      // Track which edges come from each source PTDV
      const edgesByPTDV: Record<string, Array<ProcessEdgeForValidation>> = {};

      for (const edge of incomingControlFlowEdges) {
        // Use getSourcePTDVsUpstreamOfControlFlowEdge to handle routers by looking through them to
        // find the upstream task node PTDVs.  Errors will be shown on the router -> target edge,
        // similar to other errors
        const upstreamPTDVs = getSourcePTDVsUpstreamOfControlFlowEdge(
          edge,
          flowchart,
          nodeIdToPTDVId,
          sourcePTDVIdSet
        );
        for (const ptdv of upstreamPTDVs) {
          (edgesByPTDV[ptdv] ??= []).push(edge);
        }
      }

      const duplicatePTDVs = Object.entries(edgesByPTDV)
        .filter(([, edges]) => edges.length > 1)
        .map(([ptdv]) => ptdv);
      if (duplicatePTDVs.length > 0) {
        for (const ptdv of duplicatePTDVs) {
          for (const edge of edgesByPTDV[ptdv]) {
            errors.push({
              errorMessage: '',
              errorType: ProcessFlowchartErrorType.MERGING_TRANSITION_HAS_DUPLICATE_SOURCE_PTDVS,
              blocksPersistence: true,
              object: {
                clientId: edge.id,
                type: ProcessFlowchartObjectType.EDGE,
              },
            });
          }
        }
        errors.push({
          errorMessage:
            'This merging transition has multiple source nodes of the same task type. Remove the duplicate source node.',
          errorType: ProcessFlowchartErrorType.MERGING_TRANSITION_HAS_DUPLICATE_SOURCE_PTDVS,
          blocksPersistence: true,
          object: {
            clientId: node.id,
            type: ProcessFlowchartObjectType.NODE,
          },
        });
      }
    }
  }

  return errors.length > 0 ? errors : null;
}

/**
 * While configuring transitions in the single transition canvas, validate that source node PTDVs
 * are unique. Source PTDVs are deduplicated at extraction time.
 *
 * E.g. configuring an A + A -> B transition is invalid
 */
export function ensureTransitionSourcesAreDistinct(
  flowchart: ProcessFlowchartForValidation
): Array<ProcessFlowchartValidationError> | null {
  const nodeIdToPTDVId = buildNodeIdToPTDVIdMap(flowchart.nodes);
  const nodeIdsBySourcePTDV: Record<string, Set<string>> = {};

  for (const edge of flowchart.edges) {
    if (edge.type !== ProcessFlowchartEdgeType.CONTROL_FLOW) {
      continue;
    }
    const sourcePTDV = nodeIdToPTDVId.get(edge.source)!;
    (nodeIdsBySourcePTDV[sourcePTDV] ??= new Set()).add(edge.source);
  }

  const duplicateNodeIds = Object.values(nodeIdsBySourcePTDV)
    .filter((ids) => ids.size > 1)
    .flatMap((ids) => [...ids]);

  if (duplicateNodeIds.length === 0) {
    return null;
  }

  return [
    ...duplicateNodeIds.map((nodeId) => ({
      errorMessage:
        'This transition has multiple source nodes of the same task type. Source nodes must be unique.',
      errorType: ProcessFlowchartErrorType.TRANSITION_HAS_DUPLICATE_SOURCES,
      blocksPersistence: true,
      enabledModes: [FlowchartEditorMode.SINGLE_TRANSITION_CANVAS],
      object: {clientId: nodeId, type: ProcessFlowchartObjectType.NODE},
    })),
    {
      errorMessage:
        'This transition has multiple source nodes of the same task type. Source nodes must be unique.',
      errorType: ProcessFlowchartErrorType.TRANSITION_HAS_DUPLICATE_SOURCES,
      blocksPersistence: true,
      enabledModes: [FlowchartEditorMode.SINGLE_TRANSITION_CANVAS],
      object: {clientId: flowchart.id, type: ProcessFlowchartObjectType.TRANSITION},
    },
  ];
}

/**
 * Editor modes where new transitions cannot be introduced — only edges representing
 * pre-existing transitions are allowed.
 */
const TRANSITIONS_LOCKED_VALIDATION_MODES: Array<FlowchartEditorMode> = [
  FlowchartEditorMode.ACM_EDITOR,
  FlowchartEditorMode.PCM_EDITOR,
];

/**
 * In ACM/PCM, validates that every drawn control-flow edge has a corresponding transition
 * configured for the flowchart. New transitions cannot be introduced from the ACM/PCM
 * canvas — users can only draw edges representing existing (planned or optional)
 * transitions.
 *
 * Edges to or from router nodes are validated by walking through the router: an edge
 * with a router on the source side is treated as flowing from the router's single
 * upstream task PTDV, and an edge with a router on the target side is treated as
 * flowing into every downstream task PTDV. Each implied (upstream, downstream) pair
 * must appear in some configured transition; if any implied pair is missing, the edge
 * is flagged.
 */
export function ensureEdgesHaveCorrespondingTransitions(
  flowchart: ProcessFlowchartForValidation,
  validationContext: ValidationContext
): Array<ProcessFlowchartValidationError> | null {
  const {transitions} = validationContext;

  // Build the set of (sourcePTDVId, targetPTDVId) pairs that are represented by some
  // transition. A merging transition `{A, B} -> C` contributes both `A:C` and `B:C` to
  // this set.
  const allowedSourceToTargetPairs = new Set<string>();
  for (const transition of Object.values(transitions)) {
    for (const sourcePTDVId of transition.sourcePTDVIds) {
      allowedSourceToTargetPairs.add(`${sourcePTDVId}:${transition.targetPTDVId}`);
    }
  }

  const nodeIdToPTDVId = buildNodeIdToPTDVIdMap(flowchart.nodes);

  const errors: Array<ProcessFlowchartValidationError> = [];

  for (const edge of flowchart.edges) {
    if (edge.type !== ProcessFlowchartEdgeType.CONTROL_FLOW) {
      continue;
    }

    const upstreamPTDV = resolveUpstreamPTDV(edge.source, flowchart, nodeIdToPTDVId);
    const downstreamPTDVIds = resolveDownstreamPTDVs(edge.target, flowchart, nodeIdToPTDVId);
    if (upstreamPTDV == null || downstreamPTDVIds.length === 0) {
      continue;
    }

    // Each implied (upstream, downstream) pair must be represented by some configured
    // transition. If any implied pair is missing, the edge is part of an invalid
    // routing path and is flagged.
    const allPairsConfigured = downstreamPTDVIds.every((downstreamPTDVId) =>
      allowedSourceToTargetPairs.has(`${upstreamPTDV}:${downstreamPTDVId}`)
    );
    if (allPairsConfigured) {
      continue;
    }

    errors.push({
      ...getValidationErrorForEdge(
        flowchart,
        edge,
        () => `Path '${getEdgeDisplayName(edge, flowchart)}' is not available for this flowchart.`,
        ProcessFlowchartErrorType.EDGE_HAS_NO_TRANSITION
      ),
      enabledModes: TRANSITIONS_LOCKED_VALIDATION_MODES,
    });
  }

  return errors.length > 0 ? errors : null;
}

/**
 * In ACM/PCM, validates that any merging transition whose merge has been partially drawn
 * also has all of its other source edges drawn. If a user has drawn at least one edge of
 * a merging transition `{A, B} -> C` (e.g. `A -> C`) but not the remaining edges, the
 * target node is flagged as missing required paths.
 */
export function ensureMergingTransitionsAreComplete(
  flowchart: ProcessFlowchartForValidation,
  validationContext: ValidationContext
): Array<ProcessFlowchartValidationError> | null {
  const {transitions} = validationContext;

  const mergingTransitionsByTarget: Partial<Record<string, Array<Transition>>> = {};
  for (const transition of Object.values(transitions)) {
    if (transition.sourcePTDVIds.length > 1) {
      mergingTransitionsByTarget[transition.targetPTDVId] ??= [];
      mergingTransitionsByTarget[transition.targetPTDVId]!.push(transition);
    }
  }
  if (Object.keys(mergingTransitionsByTarget).length === 0) {
    return null;
  }

  const nodeIdToPTDVId = buildNodeIdToPTDVIdMap(flowchart.nodes);

  // Pre-compute incoming control flow edges per node.
  const incomingControlFlowEdgesByNodeId = new Map<string, Array<ProcessEdgeForValidation>>();
  for (const edge of flowchart.edges) {
    if (edge.type !== ProcessFlowchartEdgeType.CONTROL_FLOW) {
      continue;
    }
    if (!incomingControlFlowEdgesByNodeId.has(edge.target)) {
      incomingControlFlowEdgesByNodeId.set(edge.target, []);
    }
    incomingControlFlowEdgesByNodeId.get(edge.target)!.push(edge);
  }

  const errors: Array<ProcessFlowchartValidationError> = [];

  for (const node of flowchart.nodes) {
    const nodePTDV = nodeIdToPTDVId.get(node.id);
    if (nodePTDV == null || !mergingTransitionsByTarget[nodePTDV]) {
      continue;
    }
    const candidateTransitions = mergingTransitionsByTarget[nodePTDV];

    const sourcePTDVIdSet = new Set(candidateTransitions.flatMap((t) => t.sourcePTDVIds));
    const incomingEdges = incomingControlFlowEdgesByNodeId.get(node.id) ?? [];
    const coveredSourcePTDVs = new Set<string>();
    for (const edge of incomingEdges) {
      for (const sourcePTDVId of getSourcePTDVsUpstreamOfControlFlowEdge(
        edge,
        flowchart,
        nodeIdToPTDVId,
        sourcePTDVIdSet
      )) {
        coveredSourcePTDVs.add(sourcePTDVId);
      }
    }
    // Only flag when the merge is partially in progress: at least one source is
    // connected but not all of them are.
    if (coveredSourcePTDVs.size === 0) {
      continue;
    }
    // Every source must belong to a fulfilled transition.  If a source does not belong to a
    // fulfilled transition, that means its merge is partially drawn
    const everyCoveredSourceIsRealized = [...coveredSourcePTDVs].every((src) =>
      candidateTransitions.some(
        (transition) =>
          transition.sourcePTDVIds.includes(src) &&
          transition.sourcePTDVIds.every((s) => coveredSourcePTDVs.has(s))
      )
    );
    if (everyCoveredSourceIsRealized) {
      continue;
    }
    errors.push({
      object: {clientId: node.id, type: ProcessFlowchartObjectType.NODE},
      errorMessage: 'This node is missing required paths.',
      errorType: ProcessFlowchartErrorType.NODE_MISSING_REQUIRED_PATH,
      blocksPersistence: true,
      severity: FlowchartValidationSeverity.WARNING,
      enabledModes: TRANSITIONS_LOCKED_VALIDATION_MODES,
    });
  }

  return errors.length > 0 ? errors : null;
}

/**
 * In the single transition canvas, a transition requires at least 2 nodes on the canvas,
 * unless the canvas has any edge (e.g. a loop on one task schema when source and target share a PTDV).
 */
export function ensureTransitionHasMultipleNodes(
  flowchart: ProcessFlowchartForValidation
): Array<ProcessFlowchartValidationError> | null {
  const taskSchemaNodes = flowchart.nodes.filter(
    (node) =>
      node.nodeType === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS ||
      node.nodeType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS ||
      node.nodeType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
  );

  if (taskSchemaNodes.length < 2 && flowchart.edges.length === 0) {
    return [
      {
        errorMessage: 'A transition requires at least 2 nodes. Add a node to continue.',
        errorType: ProcessFlowchartErrorType.TRANSITION_NEEDS_MULTIPLE_NODES,
        blocksPersistence: true,
        enabledModes: [FlowchartEditorMode.SINGLE_TRANSITION_CANVAS],
        object: {
          clientId: flowchart.id,
          type: ProcessFlowchartObjectType.TRANSITION,
        },
      },
    ];
  }

  return null;
}

export function getSourceAndTargetFieldsForDataFlowEdge(
  edge: DataFlowEdgeForValidation,
  flowchart: ProcessFlowchartForValidation
): {sourceField: FieldDefinitionNode; targetField: FieldDefinitionNode} | null {
  // Get source and target nodes
  const sourceNode = getFlowchartNodeFromClientId(flowchart, edge.source);
  const targetNode = getFlowchartNodeFromClientId(flowchart, edge.target);

  if (!sourceNode || !targetNode) {
    return null;
  }

  if (!isTaskSchemaNode(sourceNode) || !isTaskSchemaNode(targetNode)) {
    return null;
  }
  if (!sourceNode.taskDefinitionVersionSpec || !targetNode.taskDefinitionVersionSpec) {
    return null;
  }

  // Extract full field information from handles
  // For MAP_FROM_START edges, the source field comes from the root node's input field definitions,
  // not the source node's field definitions
  const sourceHandleInfo = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
  let sourceField: FieldDefinitionNode | null;

  if (sourceHandleInfo.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START) {
    // Find root node to get the field definition
    const rootNode =
      flowchart.nodes.find((node) => node.nodeType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS) ??
      flowchart.externalRootNodeStaticData;
    if (!rootNode || !isTaskSchemaNode(rootNode) || !rootNode.taskDefinitionVersionSpec) {
      return null;
    }

    const fieldDef = rootNode.taskDefinitionVersionSpec.inputFieldDefinitions?.find(
      (field) => field.fieldClientIdentifier === sourceHandleInfo.fieldClientId
    );
    sourceField = (fieldDef?.fieldDefinition as FieldDefinitionNode) ?? null;
  } else {
    sourceField = getFieldFromHandle(edge.sourceHandle, sourceNode);
  }

  const targetField = getFieldFromHandle(edge.targetHandle, targetNode);
  if (!sourceField || !targetField) {
    return null;
  }
  return {sourceField, targetField};
}

export function ensureDataFlowEdgeFieldTypesAreValid(
  edge: DataFlowEdgeForValidation,
  flowchart: ProcessFlowchartForValidation
): Array<ProcessFlowchartValidationError> | null {
  // If a custom mapping is configured, we assume the lookup handles field compatibility
  if (edge.isLookupConfigured) {
    return null;
  }

  const sourceAndTargetFields = getSourceAndTargetFieldsForDataFlowEdge(edge, flowchart);
  if (sourceAndTargetFields == null) {
    return null;
  }

  const compatibilityResult = areFieldDefinitionsCompatible(
    sourceAndTargetFields.sourceField as FieldDefinitionForValidation,
    sourceAndTargetFields.targetField as FieldDefinitionForValidation
  );

  if (!compatibilityResult.isCompatible) {
    return [
      // Return one error to render the data flow edge as red
      {
        object: {
          clientId: edge.id,
          type: ProcessFlowchartObjectType.EDGE,
        },
        errorType: ProcessFlowchartErrorType.DATA_FLOW_EDGE_MAPPING_FIELD_MISMATCH,
        blocksPersistence: true,
        errorTitle: "Field definitions don't match for one or more mappings.",
        errorMessage:
          "Configure a lookup below, or change the field definitions in the task schema's configuration.",
        enabledModes: DATA_FLOW_VALIDATION_MODES,
      },
      // Return another error for the source row / node - this is rendered on the node itself
      {
        object: {
          clientId: getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle).fieldClientId,
          type: ProcessFlowchartObjectType.ROW,
        },
        parentObjects: [
          {
            clientId: edge.source,
            type: ProcessFlowchartObjectType.NODE,
          },
        ],
        errorType: ProcessFlowchartErrorType.DATA_FLOW_EDGE_MAPPING_FIELD_MISMATCH,
        blocksPersistence: true,
        errorMessage: compatibilityResult.incompatibilityReason!,
        enabledModes: DATA_FLOW_VALIDATION_MODES,
      },
    ];
  }
  return null;
}

/**
 * Groups the source PTDVs that transition into `node` so that required-field mappings can be
 * validated independently per transition. A transition's `sourcePTDVIds` already describe exactly
 * which sources feed the target together, so each transition maps to one group: non-merging
 * transitions get a single-source group (they must populate the required fields on their own), while
 * a merging transition's sources share one group (they populate the required fields collectively).
 *
 * Returns one set of source PTDV IDs per incoming transition, or `null` to tell the caller to fall
 * back to pooling all incoming mappings together. We fall back when we're not on the main canvas
 * (e.g. the single transition canvas represents one transition), when the node isn't a resolvable
 * task PTDV, or when no transitions feed the node (e.g. data flow edges drawn before any transition
 * exists).
 */
function getIncomingTransitionSourceGroups(
  node: ProcessNodeForValidation,
  flowchart: ProcessFlowchartForValidation,
  validationContext: ValidationContext,
  nodeIdToPTDVId: ReadonlyMap<string, string>
): Array<Set<string>> | null {
  if (validationContext.editorMode !== FlowchartEditorMode.MAIN_CANVAS) {
    return null;
  }

  const targetPTDVId = nodeIdToPTDVId.get(node.id);
  if (targetPTDVId == null) {
    return null;
  }

  const upstreamPTDVIds = new Set(
    getTaskNodesUpstreamOfNode(node.id, flowchart)
      .map((upstreamNode) => nodeIdToPTDVId.get(upstreamNode.id))
      .filter((id): id is string => id != null)
  );

  const groups = Object.values(validationContext.transitions ?? {})
    .filter(
      (transition) =>
        transition.targetPTDVId === targetPTDVId &&
        transition.sourcePTDVIds.every((sourcePTDVId) => upstreamPTDVIds.has(sourcePTDVId))
    )
    .map((transition) => new Set(transition.sourcePTDVIds));

  // No transitions feed this node (e.g. data flow edges drawn before any transition exists); fall
  // back to pooling all incoming mappings.
  return groups.length > 0 ? groups : null;
}

function getRequiredFieldMappingErrorsForNode(
  node: ProcessNodeForValidation,
  flowchart: ProcessFlowchartForValidation,
  validationContext: ValidationContext,
  nodeIdToPTDVId: ReadonlyMap<string, string>
): Array<ProcessFlowchartValidationError> {
  // Only check task schema nodes that are not the root node (which cannot have edges drawn to it)
  if (!isTaskSchemaNode(node) || node.nodeType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS) {
    return [];
  }

  // In the single transition canvas, we only need to check if the target node has unmapped required fields
  // (It's fine if the source node has unmapped required fields)
  if (validationContext.editorMode === FlowchartEditorMode.SINGLE_TRANSITION_CANVAS) {
    const hasIncomingControlFlowEdge = flowchart.edges.some(
      (edge) => edge.target === node.id && edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW
    );
    if (!hasIncomingControlFlowEdge) {
      return [];
    }
  }
  const taskDefinitionVersion = node.taskDefinitionVersionSpec;
  if (!taskDefinitionVersion) {
    return [];
  }

  // Get the field definitions based on whether node is output node
  const isOutputNode = node.nodeType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS;
  const fieldDefinitions = isOutputNode
    ? taskDefinitionVersion.outputFieldDefinitions
    : taskDefinitionVersion.inputFieldDefinitions;
  if (!fieldDefinitions) {
    return [];
  }

  const requiredFields = fieldDefinitions.filter(
    (fieldDef) => fieldDef.fieldDefinition != null && fieldDef.fieldDefinition.isRequired
  );
  if (requiredFields.length === 0) {
    return [];
  }

  // Get all incoming data flow edges for this node
  const incomingDataFlowEdges = flowchart.edges.filter(
    (edge) => edge.target === node.id && edge.type === ProcessFlowchartEdgeType.DATA_FLOW
  ) as Array<DataFlowEdgeForValidation>;

  // Computes the set of target field IDs populated by the given data flow edges.
  const getMappedFieldIds = (edges: Array<DataFlowEdgeForValidation>): Set<string> => {
    const mappedFieldIds = new Set<string>();
    for (const edge of edges) {
      if (edge.targetHandle) {
        mappedFieldIds.add(getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle).fieldClientId);
      }
    }
    return mappedFieldIds;
  };

  // Each incoming transition must independently populate every required field, so a required field is
  // unmapped if any incoming transition fails to populate it. Sources that merge into this node via a
  // single merging transition populate the required fields collectively (they share a group). When we
  // can't attribute mappings to individual transitions, we pool all incoming mappings into one group.
  const sourcePTDVGroups = getIncomingTransitionSourceGroups(
    node,
    flowchart,
    validationContext,
    nodeIdToPTDVId
  );
  const edgeGroups: Array<Array<DataFlowEdgeForValidation>> =
    sourcePTDVGroups == null
      ? [incomingDataFlowEdges]
      : sourcePTDVGroups.map((sourcePTDVIds) =>
          incomingDataFlowEdges.filter((edge) => {
            const sourcePTDVId = nodeIdToPTDVId.get(edge.source);
            return sourcePTDVId != null && sourcePTDVIds.has(sourcePTDVId);
          })
        );

  const unmappedRequiredFieldClientIds = new Set<string>();
  for (const edges of edgeGroups) {
    const mappedFieldIds = getMappedFieldIds(edges);
    for (const fieldDef of requiredFields) {
      if (!mappedFieldIds.has(fieldDef.fieldClientIdentifier)) {
        unmappedRequiredFieldClientIds.add(fieldDef.fieldClientIdentifier);
      }
    }
  }

  const unmappedRequiredFields = requiredFields.filter((fieldDef) =>
    unmappedRequiredFieldClientIds.has(fieldDef.fieldClientIdentifier)
  );

  // Add individual errors for each unmapped required field (these show up as row-level errors)
  return unmappedRequiredFields.map((fieldDef) => ({
    object: {
      clientId: fieldDef.fieldClientIdentifier,
      type: ProcessFlowchartObjectType.ROW,
    },
    parentObjects: [
      {
        clientId: node.id,
        type: ProcessFlowchartObjectType.NODE,
      },
    ],
    errorType: ProcessFlowchartErrorType.NODE_MISSING_REQUIRED_PATH,
    blocksPersistence: true,
    severity: FlowchartValidationSeverity.WARNING,
    errorMessage: `Required field '${fieldDef.fieldDefinition.name}' must have a mapping from all sources.`,
    enabledModes: DATA_FLOW_VALIDATION_MODES,
  }));
}

export function ensureRequiredFieldsHaveMappings(
  flowchart: ProcessFlowchartForValidation,
  validationContext: ValidationContext
): Array<ProcessFlowchartValidationError> {
  // Build the node-id -> PTDV-id map once for the whole flowchart, then reuse it across every node
  // instead of rebuilding it per node.
  const nodeIdToPTDVId = buildNodeIdToPTDVIdMap(flowchart.nodes);
  return flowchart.nodes.flatMap((node) =>
    getRequiredFieldMappingErrorsForNode(node, flowchart, validationContext, nodeIdToPTDVId)
  );
}
